import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { NotificationEvent, NotificationAlertType, NotificationAlert, NotificationEventType, NotificationAction } from "../models/common/notification-event.model";
import { ToastService } from "../shared-new/services/toast.service";
import { EventsEmitter } from "../shared/event-emitter/EventEmitter";
import { PopupService } from "./popup.service";
import { environment } from "src/environments/environment";
import { AuthService } from "./auth.service";
import { Router } from "@angular/router";

@Injectable({ providedIn: 'root' })
export class SignalRNotificationService {

  private apiUrl = `${environment.api2BaseUrl}/api/inappnotifications`;

  private notificationsSubject = new BehaviorSubject<NotificationEvent<any>[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  private countSubject = new BehaviorSubject<number>(0);
  count$ = this.countSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private isInitialized = false;

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private popupService: PopupService,
    private eventEmitter: EventsEmitter,
    private authService: AuthService,
    private router: Router
  ) {
    // Load notifications once on service initialization
    this.loadNotifications();
  }

  // --------------------------
  // Initial Load (Once)
  // --------------------------

  /**
   * Load notifications from API - called once on initialization
   */
  private loadNotifications(includeRead: boolean = true, top: number = 100): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }
    if (this.isInitialized) {
      return; // Prevent multiple initializations
    }

    this.http.get<NotificationEvent<any>[]>(
      `${this.apiUrl}?includeRead=${includeRead}&top=${top}`,
      { responseType: 'json' }
    ).subscribe({
      next: (notifications) => {
        this.emit(notifications);
        this.isInitialized = true;
      },
      error: (err) => console.error('Failed to load notifications', err)
    });
  }

  /**
   * Manually refresh notifications from server (use sparingly)
   * Only call this after login or page reload, not continuously
   */
  public refreshNotifications(): void {
    this.isInitialized = false;
    this.loadNotifications();
  }

  // --------------------------
  // SignalR Receive Handler
  // --------------------------

  /**
   * Called by SignalR when a new notification is received in real-time
   * This is the PRIMARY way notifications are added after initial load
   */
  public receive<T>(event: NotificationEvent<T>): void {
    const alert = event.notificationAlert;

    if (!alert.notifyUser) {
      // Silent notification
      return;
    }

    const list = this.getAll();
    const exists = list.some(x => x.notificationId === event.notificationId);

    if (exists) {
      return;
    }

    // Add notification to the list
    this.addNotificationToList(event);

    // Show toast notification
    this.showToast(alert);
  }

  /**
   * Add a notification to the local list (real-time update)
   */
  private addNotificationToList<T>(event: NotificationEvent<T>): void {
    const list = this.getAll();

    // Create new event with generated ID and unread status
    const newEvent: NotificationEvent<T> = {
      ...event,
      read: false
    };

    // Add to beginning of list (newest first)
    list.unshift(newEvent);

    // Update state
    this.emit(list);
  }

  // --------------------------
  // State Management
  // --------------------------

  private emit(list: NotificationEvent<any>[]): void {
    this.notificationsSubject.next(list);
    this.countSubject.next(list.length);
    this.unreadCountSubject.next(list.filter(x => !x.read).length);
  }

  // --------------------------
  // Public API
  // --------------------------

  /**
   * Get all notifications (from local state)
   */
  getAll(): NotificationEvent<any>[] {
    return this.notificationsSubject.value;
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: number): void {
    this.http.put(`${this.apiUrl}/${id}/read`, {})
      .subscribe({
        next: () => {
          // Update local state optimistically
          const list = this.getAll();
          const item = list.find(x => x.notificationId === id);
          if (item) {
            item.read = true;
            this.emit(list);
          }
        },
        error: (err) => console.error('Failed to mark as read', err)
      });
  }

  /**
   * Mark all notifications as read
   */
  markAllRead(): void {
    this.http.put(`${this.apiUrl}/read-all`, {})
      .subscribe({
        next: () => {
          // Update local state optimistically
          const list = this.getAll();
          list.forEach(x => x.read = true);
          this.emit(list);
        },
        error: (err) => console.error('Failed to mark all as read', err)
      });
  }

  /**
   * Delete a notification
   */
  remove(notification: NotificationEvent<any>): void {
    this.http.delete(`${this.apiUrl}/${notification.notificationId}`)
      .subscribe({
        next: () => {
          // Update local state optimistically
          const list = this.getAll().filter(x => x.notificationId !== notification.notificationId);
          this.emit(list);
        },
        error: (err) => console.error('Failed to delete notification', err)
      });
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.http.delete(`${this.apiUrl}/clear-all`)
      .subscribe({
        next: () => {
          // Clear local state
          this.emit([]);
        },
        error: (err) => console.error('Failed to clear all notifications', err)
      });
  }

  // --------------------------
  // Toast Notifications
  // --------------------------

  private showToast(alert: NotificationAlert): void {
    switch (alert.type) {
      case NotificationAlertType.Success:
        this.toast.success(alert.message, alert.title, {} , true);
        break;
      case NotificationAlertType.Warning:
        this.toast.warning(alert.message, alert.title, true, {}, true);
        break;
      case NotificationAlertType.Error:
        this.toast.error(alert.message, alert.title, {}, true);
        break;
      case NotificationAlertType.Info:
        this.toast.info(alert.message, alert.title, {}, true);
        break;
    }
  }

  // --------------------------
  // Navigation
  // --------------------------

  public openNotificationDetail(notification: NotificationEvent<any>): void {
    // Mark as read when opening
    if (!notification.read) {
      this.markAsRead(notification.notificationId);
    }

    switch (notification.eventType) {
      case NotificationEventType.StudySubmitted:
        this.popupService.openStudyPopup(notification);
        break;
      case NotificationEventType.SaleAccess:
        if (notification.action === NotificationAction.Requested) {
          this.popupService.reviewVetInfo(notification);
        }
        if (notification.action === NotificationAction.Approved || notification.action === NotificationAction.Denied) {
          this.eventEmitter.emitOnSaleRequestApproveOrDeniedEvent();
        }
        break;
      case NotificationEventType.SaleResult:
        break;
      case NotificationEventType.DownloadStatusUpdate:
        this.navigateToDownloadPage(notification.scope.saleId);
        break;
    }
  }

  private navigateToDownloadPage(saleId: number): void {
    this.router.navigate(['/clinic-studies/download-localstorage'], {
      queryParams: { saleId: saleId }
    });
  }
}
